<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';

/**
 * A player-log message line: shown in full up to 3 lines, then clamped with a
 * Show more / Show less toggle. Log messages range from a short "PLAY: …" line
 * to whole-file command output (many lines), so only entries that actually
 * exceed 3 lines get a toggle — shorter ones render as-is with no affordance.
 * `whitespace-pre-wrap` keeps embedded newlines; `break-words` wraps long
 * unbreakable tokens (track codes, paths).
 *
 * The date floats top-right INSIDE the message so the text wraps around it on
 * the first line and reclaims full width below. That's why the clamp is
 * max-height (3 lines) + overflow-hidden rather than -webkit-line-clamp:
 * -webkit-box establishes a flex-ish context that won't flow text around a
 * float, so the two are mutually exclusive.
 */
const props = defineProps<{
    text: string;
    /** Dimmed timestamp floated at the top-right of the message. */
    date?: string;
    /** Native hover tooltip for the collapsed state (dropped once expanded). */
    title?: string;
    /**
     * The playlist filename inside this line, when it has one. Rendered as a
     * link in place of the plain text so the list it names can be opened
     * without copying the filename out of the log.
     */
    playlistName?: string | null;
}>();

const emit = defineEmits<{ (e: 'open-playlist', playlistName: string): void }>();

/**
 * The line split around the playlist filename. Only the FIRST occurrence is
 * linked — the filename appears once, and splitting on every match would turn
 * a repeated token into several buttons that all do the same thing.
 */
const messageParts = computed<{ before: string; link: string | null; after: string }>(() => {
    const name = props.playlistName;
    if (!name) {
        return { before: props.text, link: null, after: '' };
    }
    const at = props.text.indexOf(name);
    if (at === -1) {
        return { before: props.text, link: null, after: '' };
    }
    return {
        before: props.text.slice(0, at),
        link: name,
        after: props.text.slice(at + name.length),
    };
});

const expanded = ref(false);
const overflowing = ref(false);
const textEl = ref<HTMLElement | null>(null);

/**
 * Does the clamped text exceed 3 lines? Measured only while collapsed —
 * expanding removes the clamp, so re-measuring then would always read false;
 * we preserve the collapsed measurement so the toggle stays put.
 */
function measure(): void {
    if (expanded.value) {
        return;
    }
    const element = textEl.value;
    if (!element) {
        return;
    }
    overflowing.value = element.scrollHeight > element.clientHeight + 1;
}

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
    nextTick(measure);
    if (typeof ResizeObserver !== 'undefined' && textEl.value) {
        resizeObserver = new ResizeObserver(() => measure());
        resizeObserver.observe(textEl.value);
    }
});

onBeforeUnmount(() => resizeObserver?.disconnect());

watch(() => props.text, () => nextTick(measure));
</script>

<template>
    <div class="min-w-0">
        <!-- max-height clamp (not line-clamp) so the floated date lets the text
             wrap around it; overflow-hidden trims to 3 lines when collapsed. -->
        <div
            ref="textEl"
            data-testid="lzp-log-message-text"
            class="whitespace-pre-wrap break-words text-xs"
            :class="expanded ? '' : 'max-h-12 overflow-hidden'"
            :title="expanded ? undefined : title"
        ><span
            v-if="date"
            class="text-muted-foreground float-right ml-3 whitespace-nowrap text-[11px] leading-4 tabular-nums"
        >{{ date }}</span><template v-if="messageParts.link === null">{{ text }}</template><template
            v-else
        >{{ messageParts.before }}<button
            type="button"
            class="hover:text-primary cursor-pointer underline underline-offset-2 focus-visible:outline-none"
            data-testid="lzp-log-playlist-open"
            @click="emit('open-playlist', messageParts.link)"
        >{{ messageParts.link }}</button>{{ messageParts.after }}</template></div>
        <button
            v-if="overflowing"
            type="button"
            class="text-muted-foreground hover:text-foreground mt-0.5 text-[11px] font-medium"
            data-testid="lzp-log-message-toggle"
            @click="expanded = !expanded"
        >
            {{ expanded ? 'Show less' : 'Show more' }}
        </button>
    </div>
</template>
